/**
 * Gestion › Suivi des réservations — the manager's booking queue.
 *
 * Two views of one list: « À valider » (the pending requests, the reason an
 * admin opens this page) and « Toutes » (every booking, filterable by état
 * and period). The view is in the URL; with none given it opens on « À
 * valider » when something is waiting and on « Toutes » otherwise, so an
 * empty queue never greets the admin with an empty table.
 *
 * `amenityId` narrows everything to one équipement — the Réglages capacity
 * conflict alert links here with it.
 *
 * « Planning » is the third view: one day (`date`, default today on the org
 * calendar), every équipement on a time grid — the front desk's « who is in
 * which salle » screen. It ignores the list filters.
 */
import type { RouterOutputs } from "@gately/api";
import type { ColumnDef } from "@gately/ui/data-table";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { z } from "zod/v4";

import { cn } from "@gately/ui";
import { Button } from "@gately/ui/button";
import { DataTable } from "@gately/ui/data-table";
import { DateTimePicker } from "@gately/ui/date-time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { StatusBadge } from "@gately/ui/status-badge";
import { BOOKING_STATUSES, bookingStatusLabel } from "@gately/validators";

import { BookingDetailSheet } from "~/components/booking/BookingDetailSheet";
import { BookingPlanning } from "~/components/booking/BookingPlanning";
import { BookOnBehalfDialog } from "~/components/booking/BookOnBehalfDialog";
import { RejectBookingDialog } from "~/components/booking/RejectBookingDialog";
import {
  bookingRangeLabel,
  bookingStatusTone,
  todayKey,
} from "~/lib/booking-format";
import { useApproveBooking } from "~/lib/booking-queries";
import { dateKey } from "~/lib/dashboard-format";
import { orpc } from "~/lib/orpc";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

const DATE_KEY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const TABS = ["a_valider", "planning", "toutes"] as const;
type Tab = (typeof TABS)[number];

const searchSchema = z.object({
  amenityId: z.string().min(1).optional().catch(undefined),
  // From the shared tuple, so every option the toolbar renders is a value
  // this schema keeps and the server's input schema accepts.
  status: z.enum(BOOKING_STATUSES).optional().catch(undefined),
  from: DATE_KEY.optional().catch(undefined),
  to: DATE_KEY.optional().catch(undefined),
  tab: z.enum(TABS).optional().catch(undefined),
  /** The planning's day. */
  date: DATE_KEY.optional().catch(undefined),
});

type BookingSearch = z.infer<typeof searchSchema>;

/** « Tous » needs a value: Radix rejects "" as a SelectItem value. The
 *  sentinels never leave this file. */
const ANY_STATUS = "tous";
const ANY_AMENITY = "tous";

/** The cap `booking.listForAdmin` applies. Mirrored only to decide whether
 *  the truncation notice is warranted. */
const PAGE_CAP = 500;

export const Route = createFileRoute("/_dashboard/gestion-reservations")({
  validateSearch: (s) => searchSchema.parse(s),
  // Module on AND manager. The server enforces both (amenityAdminProcedure);
  // hiding the page is a courtesy, never the boundary.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canManageAmenities) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  // The count decides the default view, so it is in hand before the first
  // render — otherwise the page would open on one tab and jump to the other.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      orpc.booking.pendingCount.queryOptions(),
    ),
  component: GestionReservationsPage,
});

type BookingRow = RouterOutputs["booking"]["listForAdmin"][number];

function listInput(search: BookingSearch, tab: Tab) {
  return {
    ...(search.amenityId ? { amenityId: search.amenityId } : {}),
    ...(tab === "a_valider"
      ? { status: "pending" as const }
      : search.status
        ? { status: search.status }
        : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
  };
}

function GestionReservationsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const terms = useTerms();

  const [openId, setOpenId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<BookingRow | null>(null);
  /** Open « Réserver pour un membre », with what the planning pre-selects. */
  const [onBehalf, setOnBehalf] = useState<{
    amenityId?: string;
    date?: string;
  } | null>(null);
  const timeZone = useOrgTimeZone();
  const planningDate = search.date ?? todayKey(timeZone);

  const pending = useQuery(orpc.booking.pendingCount.queryOptions());
  const pendingCount = pending.data?.count ?? 0;
  const tab: Tab = search.tab ?? (pendingCount > 0 ? "a_valider" : "toutes");

  const query = useQuery({
    ...orpc.booking.listForAdmin.queryOptions({
      input: listInput(search, tab),
    }),
    enabled: tab !== "planning",
  });
  const amenities = useQuery(orpc.amenity.list.queryOptions());
  const approve = useApproveBooking();

  // Data first, error second: React Query keeps `data` through a FAILED
  // REFETCH, so testing `isError` first would blank a loaded table on a blip.
  const rows = query.data ?? [];
  const failed = query.data === undefined && query.isError;
  const anyMultiSeat = rows.some((r) => r.seats > 1);

  function setFilter(patch: Partial<BookingSearch>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  const columns: ColumnDef<BookingRow>[] = [
    {
      id: "when",
      accessorFn: (r) => `${r.date} ${String(r.startMinutes).padStart(4, "0")}`,
      size: 210,
      minSize: 170,
      enableHiding: false,
      meta: { label: "Date" },
      header: "Date",
      // The date IS the affordance: DataTable has no row-click hook, and a
      // whole clickable row would swallow text selection in the other cells.
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => setOpenId(row.original.id)}
          className="text-left font-medium whitespace-nowrap hover:underline"
        >
          {bookingRangeLabel(row.original)}
        </button>
      ),
    },
    {
      accessorKey: "amenityName",
      size: 180,
      minSize: 130,
      meta: { label: "Équipement" },
      header: "Équipement",
    },
    {
      accessorKey: "bookerName",
      size: 170,
      minSize: 130,
      meta: { label: terms.bookerLabel },
      header: terms.bookerLabel,
      // `booker_user_id` is ON DELETE SET NULL: the booking outlives the
      // account, and « — » would read as « nobody booked this ».
      cell: ({ getValue }) =>
        getValue<string | null>() ?? (
          <span className="text-muted-foreground">Compte supprimé</span>
        ),
    },
    {
      accessorKey: "unitLabel",
      size: 140,
      minSize: 110,
      meta: { label: terms.unitRowLabel },
      header: terms.unitRowLabel,
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    ...(anyMultiSeat
      ? ([
          {
            accessorKey: "seats",
            size: 90,
            minSize: 80,
            meta: { label: "Places", align: "right" },
            header: "Places",
          },
        ] satisfies ColumnDef<BookingRow>[])
      : []),
    {
      accessorKey: "status",
      size: 130,
      minSize: 110,
      meta: { label: "État" },
      header: "État",
      cell: ({ getValue }) => {
        const v = getValue<BookingRow["status"]>();
        return (
          <StatusBadge tone={bookingStatusTone(v)}>
            {bookingStatusLabel(v)}
          </StatusBadge>
        );
      },
    },
    {
      accessorKey: "inviteCount",
      size: 90,
      minSize: 80,
      meta: { label: "Invités", align: "right" },
      header: "Invités",
      cell: ({ getValue }) => {
        const n = getValue<number>();
        return n > 0 ? n : <span className="text-muted-foreground">—</span>;
      },
    },
    {
      id: "actions",
      size: 190,
      minSize: 170,
      enableHiding: false,
      enableSorting: false,
      meta: { label: "Actions" },
      header: "",
      cell: ({ row }) =>
        row.original.status === "pending" ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={approve.isPending}
              onClick={() => setRejecting(row.original)}
            >
              Refuser
            </Button>
            <Button
              size="sm"
              disabled={approve.isPending}
              onClick={() => approve.mutate({ id: row.original.id })}
            >
              Valider
            </Button>
          </div>
        ) : null,
    },
  ];

  const isFiltered =
    search.amenityId !== undefined ||
    search.from !== undefined ||
    search.to !== undefined ||
    (tab === "toutes" && search.status !== undefined);

  return (
    <div className="px-7 py-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
            Suivi des réservations
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Les demandes à valider et toutes les réservations des équipements.
          </p>
        </div>
        <Button
          onClick={() =>
            setOnBehalf(
              tab === "planning"
                ? { date: planningDate }
                : { amenityId: search.amenityId },
            )
          }
        >
          <Plus />
          Réserver pour un membre
        </Button>
      </div>

      <div className="mb-4 inline-flex items-center rounded-lg border border-neutral-200 bg-white p-0.5">
        {(
          [
            { key: "a_valider", label: `À valider (${pendingCount})` },
            { key: "planning", label: "Planning" },
            { key: "toutes", label: "Toutes" },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setFilter({ tab: t.key })}
            aria-pressed={tab === t.key}
            className={cn(
              "rounded-[7px] px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t.key
                ? "bg-neutral-900 text-white"
                : "text-neutral-500 hover:text-neutral-900",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "planning" ? (
        <BookingPlanning
          date={planningDate}
          onDateChange={(date) => setFilter({ date })}
          onOpenBooking={setOpenId}
          onBook={(amenityId) => setOnBehalf({ amenityId, date: planningDate })}
        />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          isLoading={query.isPending}
          initialPinning={{ left: ["when"], right: ["actions"] }}
          showColumnToggle
          // Plain URL-bound controls, not DataTable faceted chips: a chip means
          // `columnFilters`, and DataTable swaps the caller's `empty` for its
          // generic « Aucun résultat » while one is active — which would turn a
          // failed load into a statement about the bookings (see colis.tsx).
          toolbar={
            <>
              <Select
                value={search.amenityId ?? ANY_AMENITY}
                onValueChange={(v) =>
                  setFilter({ amenityId: v === ANY_AMENITY ? undefined : v })
                }
              >
                <SelectTrigger
                  size="sm"
                  className="w-48 data-[size=sm]:h-9"
                  aria-label="Équipement"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_AMENITY}>
                    Tous les équipements
                  </SelectItem>
                  {(amenities.data ?? []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tab === "toutes" ? (
                <Select
                  value={search.status ?? ANY_STATUS}
                  onValueChange={(v) =>
                    setFilter({
                      status: BOOKING_STATUSES.find((s) => s === v),
                    })
                  }
                >
                  <SelectTrigger
                    size="sm"
                    className="w-40 data-[size=sm]:h-9"
                    aria-label="État"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_STATUS}>Tous les états</SelectItem>
                    {BOOKING_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {bookingStatusLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <DateTimePicker
                mode="date"
                className="h-9 w-36"
                value={
                  search.from ? new Date(`${search.from}T00:00:00`) : undefined
                }
                onChange={(d) =>
                  setFilter({ from: d ? dateKey(d) : undefined })
                }
                placeholder="Du"
              />
              <DateTimePicker
                mode="date"
                className="h-9 w-36"
                value={
                  search.to ? new Date(`${search.to}T00:00:00`) : undefined
                }
                onChange={(d) => setFilter({ to: d ? dateKey(d) : undefined })}
                placeholder="Au"
              />
              {search.from || search.to ? (
                <button
                  type="button"
                  onClick={() => setFilter({ from: undefined, to: undefined })}
                  className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
                >
                  Effacer les dates
                </button>
              ) : null}
            </>
          }
          empty={
            // A failed load must never read as « aucune réservation » — that is
            // a statement about the équipements, and a lie about the network.
            failed
              ? {
                  title: "Impossible de charger les réservations.",
                  description:
                    "Vérifiez votre connexion, puis rechargez la page. Si le problème persiste, contactez Gately.",
                }
              : tab === "a_valider"
                ? {
                    title: "Aucune demande à valider",
                    description: isFiltered
                      ? "Élargissez la période ou changez d'équipement."
                      : "Les demandes sur les équipements soumis à validation apparaissent ici.",
                  }
                : isFiltered
                  ? {
                      title: "Aucune réservation ne correspond",
                      description:
                        "Élargissez la période, changez l'état ou l'équipement.",
                    }
                  : {
                      title: "Aucune réservation pour l'instant.",
                      description:
                        "Chaque réservation d'un équipement apparaît ici, qu'elle soit faite par un membre ou pour lui.",
                    }
          }
          footer={
            rows.length === PAGE_CAP ? (
              <>
                Affichage limité aux {PAGE_CAP} réservations les plus récentes —
                resserrez la période pour voir les plus anciennes.
              </>
            ) : null
          }
        />
      )}

      <BookingDetailSheet
        bookingId={openId}
        open={openId !== null}
        onOpenChange={(o) => {
          if (!o) setOpenId(null);
        }}
        mode="admin"
      />
      {rejecting ? (
        <RejectBookingDialog
          bookingId={rejecting.id}
          summary={`${rejecting.amenityName} · ${bookingRangeLabel(rejecting)}`}
          open
          onOpenChange={(o) => {
            if (!o) setRejecting(null);
          }}
        />
      ) : null}
      {onBehalf ? (
        // Mounted per opening, so the planning's pre-selection is the
        // dialog's initial state rather than a stale one.
        <BookOnBehalfDialog
          open
          onOpenChange={(o) => {
            if (!o) setOnBehalf(null);
          }}
          defaultAmenityId={onBehalf.amenityId}
          defaultDate={onBehalf.date}
        />
      ) : null}
    </div>
  );
}
